import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiChevronLeft, FiChevronRight, FiTrendingUp, FiDollarSign, FiPlay, FiPause } = FiIcons;

const ResultsCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const autoPlayRef = useRef(null);
  const intervalDuration = 5000; // 5 seconds per slide

  // Setup autoplay functionality
  useEffect(() => {
    const startAutoPlay = () => {
      autoPlayRef.current = setInterval(() => {
        if (isAutoPlaying) {
          setCurrentSlide((prev) => (prev + 1) % results.length);
        }
      }, intervalDuration);
    };

    // Start autoplay
    startAutoPlay();

    // Cleanup function to clear interval when component unmounts
    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [isAutoPlaying]); // Re-run effect when isAutoPlaying changes

  // Reset the timer when manually changing slides
  const resetAutoPlayTimer = () => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
      if (isAutoPlaying) {
        autoPlayRef.current = setInterval(() => {
          setCurrentSlide((prev) => (prev + 1) % results.length);
        }, intervalDuration);
      }
    }
  };

  // Pause autoplay when user interacts with carousel
  const toggleAutoPlay = () => {
    setIsAutoPlaying(prev => !prev);
  };

  const results = [
    {
      type: "before-after",
      metric: "CTR",
      before: "1.2%",
      after: "4.8%",
      icon: FiTrendingUp,
      description: "Click-through rate improvement"
    },
    {
      type: "before-after",
      metric: "ROI",
      before: "120%",
      after: "320%",
      icon: FiDollarSign,
      description: "Return on investment growth"
    },
    {
      type: "video",
      title: "Campaign Success Story",
      description: "Watch how we transformed a client's business",
      icon: FiPlay
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % results.length);
    resetAutoPlayTimer();
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + results.length) % results.length);
    resetAutoPlayTimer();
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    resetAutoPlayTimer();
  };

  return (
    <section className="py-24 relative" style={{ backgroundColor: '#3a1c40' }}>
      {/* Animated background elements */}
      <motion.div 
        className="absolute inset-0 w-full h-full"
        style={{ 
          background: 'radial-gradient(circle at 70% 50%, rgba(177, 107, 189, 0.15) 0%, rgba(58, 28, 64, 0) 50%)',
        }}
        animate={{
          opacity: [0.5, 0.8, 0.5]
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      
      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.h2 
            className="text-4xl md:text-5xl font-bold mb-6 text-purple-100"
            animate={{ 
              textShadow: [
                "0 0 0px rgba(177, 107, 189, 0)",
                "0 0 15px rgba(177, 107, 189, 0.5)",
                "0 0 0px rgba(177, 107, 189, 0)"
              ]
            }}
            transition={{
              duration: 3,
              repeat: Infinity
            }}
          >
            Results That Speak for Themselves
          </motion.h2>
        </motion.div>

        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, x: 100, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: -100, scale: 0.9 }}
              transition={{ 
                duration: 0.6,
                type: "spring",
                stiffness: 100
              }}
              className="bg-gradient-to-r from-purple-900/30 to-purple-800/30 backdrop-blur-sm rounded-3xl p-12 text-center border border-purple-700"
            >
              {results[currentSlide].type === 'before-after' ? (
                <div className="space-y-8">
                  <motion.div 
                    className="flex justify-center"
                    animate={{ 
                      y: [0, -10, 0],
                      scale: [1, 1.1, 1]
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    <SafeIcon 
                      icon={results[currentSlide].icon} 
                      className="text-6xl" 
                      style={{ color: '#FFD700' }} 
                    />
                  </motion.div>
                  <h3 className="text-3xl font-bold text-purple-100">
                    {results[currentSlide].metric} Improvement
                  </h3>
                  <div className="flex justify-center items-center space-x-12">
                    <div className="text-center">
                      <p className="text-sm text-purple-300 mb-2">BEFORE</p>
                      <p className="text-4xl font-bold text-red-400">{results[currentSlide].before}</p>
                    </div>
                    <motion.div 
                      className="text-6xl text-yellow-400"
                      animate={{ 
                        x: [0, 10, 0],
                        scale: [1, 1.1, 1]
                      }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    >
                      →
                    </motion.div>
                    <div className="text-center">
                      <p className="text-sm text-purple-300 mb-2">AFTER</p>
                      <motion.p 
                        className="text-4xl font-bold text-green-400"
                        animate={{ 
                          textShadow: [
                            "0 0 0px rgba(74, 222, 128, 0)",
                            "0 0 10px rgba(74, 222, 128, 0.7)",
                            "0 0 0px rgba(74, 222, 128, 0)"
                          ]
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity
                        }}
                      >
                        {results[currentSlide].after}
                      </motion.p>
                    </div>
                  </div>
                  <p className="text-lg text-purple-200">{results[currentSlide].description}</p>
                </div>
              ) : (
                <div className="space-y-8">
                  <div className="flex justify-center">
                    <motion.div 
                      className="w-32 h-32 rounded-full flex items-center justify-center cursor-pointer"
                      style={{ backgroundColor: '#FFD700' }}
                      whileHover={{ scale: 1.1 }}
                      animate={{ 
                        boxShadow: [
                          "0 0 0px rgba(255, 215, 0, 0)",
                          "0 0 30px rgba(255, 215, 0, 0.7)",
                          "0 0 0px rgba(255, 215, 0, 0)"
                        ]
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity
                      }}
                    >
                      <SafeIcon 
                        icon={results[currentSlide].icon} 
                        className="text-4xl" 
                        style={{ color: '#3a1c40' }} 
                      />
                    </motion.div>
                  </div>
                  <h3 className="text-3xl font-bold text-purple-100">
                    {results[currentSlide].title}
                  </h3>
                  <p className="text-lg text-purple-200">{results[currentSlide].description}</p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons */}
          <motion.button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 p-3 rounded-full bg-purple-800 bg-opacity-50 hover:bg-opacity-80 text-white backdrop-blur-sm border border-purple-700 hover:border-yellow-400 transition-all duration-300"
            whileHover={{ scale: 1.1, x: -3 }}
            whileTap={{ scale: 0.95 }}
          >
            <SafeIcon icon={FiChevronLeft} className="text-2xl" />
          </motion.button>
          <motion.button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 p-3 rounded-full bg-purple-800 bg-opacity-50 hover:bg-opacity-80 text-white backdrop-blur-sm border border-purple-700 hover:border-yellow-400 transition-all duration-300"
            whileHover={{ scale: 1.1, x: 3 }}
            whileTap={{ scale: 0.95 }}
          >
            <SafeIcon icon={FiChevronRight} className="text-2xl" />
          </motion.button>

          {/* Progress Bar */}
          <div className="mt-6 relative h-1.5 bg-purple-800/50 rounded-full overflow-hidden mx-auto max-w-md">
            {isAutoPlaying && (
              <motion.div 
                className="absolute top-0 left-0 h-full bg-yellow-400"
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                key={currentSlide}
                transition={{ 
                  duration: intervalDuration / 1000, 
                  ease: "linear",
                }}
              />
            )}
          </div>

          {/* Controls */}
          <div className="flex justify-between items-center mt-4">
            {/* Dots Indicator */}
            <div className="flex justify-center space-x-3 mx-auto">
              {results.map((_, index) => (
                <motion.button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300`}
                  style={{ 
                    backgroundColor: index === currentSlide ? '#FFD700' : 'rgba(255, 255, 255, 0.3)'
                  }}
                  animate={index === currentSlide ? {
                    scale: [1, 1.3, 1],
                    opacity: [0.7, 1, 0.7],
                  } : {}}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  whileHover={{ scale: 1.5 }}
                />
              ))}
            </div>

            {/* Play/Pause Button */}
            <motion.button
              onClick={toggleAutoPlay}
              className="absolute right-0 -bottom-10 p-2 rounded-full bg-purple-800/50 hover:bg-purple-700/70 text-white backdrop-blur-sm border border-purple-700 hover:border-yellow-400 transition-all duration-300"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              title={isAutoPlaying ? "Pause" : "Play"}
            >
              <SafeIcon icon={isAutoPlaying ? FiPause : FiPlay} className="text-sm" />
            </motion.button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResultsCarousel;